import { persistReducer, createTransform } from "redux-persist";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
// import createExpirationTransform from "redux-persist-transform-expire";
import { DateTime } from ".";
import moment from "moment";
import { CircularJSON } from "./json";
import { types as setuptypes } from "../boot/setupRedux";

/** https://github.com/rt2zz/redux-persist/blob/master/docs/api.md#type-persistconfig
 * {
 * version?: number, // the state version as an integer (defaults to -1)
 * blacklist?: Array<string>, // do not persist these keys
 * whitelist?: Array<string>, // only persist these keys
 * migrate?: (Object, number) => Promise<Object>,
 * transforms?: Array<Transform>,
 * throttle?: number, // ms to throttle state writes
 * keyPrefix?: string, // will be prefixed to the storage key
 * debug?: boolean, // true -> verbose logs
 * stateReconciler?: false | StateReconciler, // false -> do not automatically reconcile state
 * }
 */
const defaultPersistConfig = {
	whitelist: undefined,
	blacklist: undefined,
	expireInNumHours: 1,
	circularJson: undefined //an array of keys to serialize as json with circular refs. Much slower tho
};

const defaultPersistTypeConf = {
	passStateToCtor: false, //pass JS object parsed from storage to the type's ctor as the first arg in the args pack
	passArgsAtPos: 0 //pass ctor arguments at arg number. (all prior args will recieve null)
};

/**
 * Add an es6 class Type to the persist map.
 * If this class is persisted, it will be parsed from storage as this type.
 * @param {object} type Type to persist
 * @param {object} conf See `defaultPersistTypeConf` object for options
 * @param {any} args Args to pass to the ctor when read from storage.
 */
export function persistType(id, type, conf, ...args) {
	conf = { ...defaultPersistTypeConf, ...conf };

	if (type.prototype._PERSIST_AS_TYPE) {
		throw new Error("The Type '" + type.prototype._PERSIST_AS_TYPE + "' has already been persisted");
	}

	type.prototype._PERSIST_AS_TYPE = id;
	type.prototype._constructor = type.prototype.constructor;
	const oldProto = type.prototype;
	type = function(...args) {
		let item = new type.prototype._constructor(...args);
		item._PERSIST_AS_TYPE = type.prototype._PERSIST_AS_TYPE;
		return item;
	};
	oldProto.constructor = type.prototype.constructor;
	type.prototype = oldProto;
	if (persistTypes.find(x => x.type.prototype._PERSIST_AS_TYPE === id)) {
		throw new Error("A Type with name '" + id + "' has already been persisted");
	}
	persistTypes.push({ type, args, conf });
	return type;
}

/**
 * Automatically persist a given reducer. Returns the persisted reducer function ready for MergeReducers.
 * @param {string} name Name of prop in state/parent reducer
 * @param {function} reducer Reducer to persist
 * @param {object} config Config. See `defaultPersistConfig` object in this file for options
 */
export default function persist(name, reducer, config) {
	config = { ...defaultPersistConfig, ...config, serialize: false };

	const timeFactory = () => {
		return moment()
			.add(config.expireInNumHours * 60, "minutes")
			.toDate()
			.toISOString();
	};

	const newTime = timeFactory();
	if (config.expireInNumHours > 0) {
		_persistKeys.push("persist_timeout:" + name);
	}

	const expireTransform = createExpirationTransform(name, timeFactory, config);
	const typesTransform = createTypesTransform(name, config);

	const transforms = config.expireInNumHours > 0 ? [expireTransform, typesTransform] : [typesTransform];

	const persistConfig = {
		...config,
		key: name,
		storage: createStorage(config),
		transforms
	};

	const persistedReducer = (state, action) => {
		if (action.type === setuptypes.CLEAR_STATE) {
			//if the state was cleared we need to return the default state so persist continues to work
			//for some reason having the default: => state in each reducer doesnt cut it
			return state;
		}
		return reducer(state, action);
	};

	return { [name]: persistReducer(persistConfig, persistedReducer) };
}

//create a custom store so we can parse objects with circular references
function createStorage(config) {
	return {
		setItem: async function(key, val) {
			if (config.circularJson && config.circularJson.find(x => x === key)) {
				val = CircularJSON.stringify(val);
			} else {
				val = JSON.stringify(val);
			}
			await localStorage.setItem(key, val);
		},
		getItem: async function(key) {
			const item = await localStorage.getItem(key);
			if (config.circularJson && config.circularJson.find(x => x === key)) {
				return CircularJSON.parse(item);
			} else {
				return JSON.parse(item);
			}
		},
		removeItem: async function(key) {
			await localStorage.removeItem(key);
		}
	};
}

//create a timeout key whenever this reducer is fired.
function createExpirationTransform(name, timeFactory, config) {
	function inbound(state, key) {
		if (!state || /^[_\$@&]/.test(key)) {
			return state;
		}
		const newTime = timeFactory();
		localStorage.setItem("persist_timeout:" + name, newTime);
		return state;
	}

	function outbound(state, key) {
		return state;
	}

	return createTransform(inbound, outbound);
}

//a transform that correctly unserializes Date types + any types recorded with persistType()
function createTypesTransform(name, config) {
	function inbound(state, key) {
		if (!state || /^[_\$@&]/.test(key)) {
			return state;
		}
		return state;
	}

	function outbound(state, key) {
		if (!state || /^[_\$@&]/.test(key)) {
			return state;
		}

		function traverse(obj) {
			if (Array.isArray(obj)) {
				for (let index = 0; index < obj.length; index++) {
					const item = obj[index];
					obj[index] = traverse(item);
				}
			} else if (typeof obj === "object" && obj !== null) {
				obj = parseObjAsType(obj);

				for (var prop in obj) {
					if (obj.hasOwnProperty(prop)) {
						//unserialize Date objects
						if (typeof obj[prop] === "string" && DateTime.isDateString(obj[prop])) {
							obj[prop] = new Date(obj[prop]);
						} else if (typeof obj[prop] === "object") {
							obj[prop] = traverse(obj[prop]);
						}
					}
				}
			}
			return obj;
		}

		return traverse(state);
	}

	return createTransform(inbound, outbound);
}

// traverse object and see if type has been registered with persistType
function parseObjAsType(state) {
	if (typeof state === "object" && state !== null) {
		let astype = null;
		if (state._PERSIST_AS_TYPE !== undefined) {
			astype = dynamicClass(state, state._PERSIST_AS_TYPE);
		}

		if (astype) {
			for (var prop in state) {
				if (state.hasOwnProperty(prop)) {
					const item = state[prop];
					astype[prop] = item;
				}
			}
			state = astype;
		}

		return state;
	}
}

function dynamicClass(state, id) {
	const item = persistTypes.find(x => x.type.prototype._PERSIST_AS_TYPE === id);
	if (item) {
		let args = item.args;
		if (item.conf.passStateToCtor) {
			args.unshift(state);
		}
		if (!isNaN(item.conf.passArgsAtPos)) {
			for (let index = 0; index < item.conf.passArgsAtPos; index++) {
				args.unshift(null);
			}
		}
		return new item.type(...args);
	} else {
		return null;
	}
}

// Use ES6 Object Literal Property Value Shorthand to maintain a map
// where the keys share the same names as the classes themselves
let persistTypes = [];

const _persistKeys = [];

/**
 * Delete any persists that have not been updated within their givin time window
 */
export async function deleteExpiredPersistsAsync() {
	for (let index = 0; index < _persistKeys.length; index++) {
		const pKey = _persistKeys[index];
		const time = await localStorage.getItem(pKey);
		if (time && new Date(time) < new Date()) {
			const name = /\:.*/.exec(pKey);
			await localStorage.removeItem(pKey);
			await localStorage.removeItem("persist" + name);
			console.log("Purge redux: " + name);
		}
	}
}



// WEBPACK FOOTER //
// ./src/utils/persist.js